import type { ARRecoveryStatementRecord } from "./arRecoveryData";

export interface CanonicalStatementApiRecord {
  companyId: string;
  clientId?: string | null;
  customerId: string;
  accountId: string;
  statementId: string;
  statementNumber: string;
  statementType?: string;
  statementDate?: string | null;
  asOfDate?: string | null;
  currency?: string;
  balanceCents: number;
  openInvoiceCount: number;
  recipientName?: string | null;
  recipientEmail?: string | null;
  deliveryOption?: string;
  lifecycleStatus: string;
  deliveryStatus: string;
  openedStatus?: string;
  downloadedStatus?: string;
  customerName: string;
  customerNumber: string;
  clientCompany?: string | null;
  collector?: string | null;
  manager?: string | null;
  notes?: string | null;
  mergeFields?: Record<string, unknown>;
  lineItems?: Array<{
    invoiceId?: string | null;
    invoiceNumber: string;
    invoiceDate?: string | null;
    dueDate?: string | null;
    originalAmountCents: number;
    paymentsAppliedCents?: number;
    creditsAppliedCents?: number;
    adjustmentsAppliedCents?: number;
    remainingBalanceCents?: number;
    agingBucket?: string | null;
    description?: string | null;
    sortOrder?: number;
  }>;
  generatedAt?: string | null;
  generatedByUserId?: string | null;
  generatedByName?: string | null;
  sentAt?: string | null;
  sentByName?: string | null;
  archivedAt?: string | null;
  source?: string;
  sourceType?: string | null;
  sourceSystem?: string | null;
  financialTruthOwner?: string | null;
  sourceSnapshotHash?: string | null;
  legacyImportMetadata?: Record<string, unknown>;
  version?: number;
  idempotencyKey?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  placementId?: string | null;
  activeDocumentVersion?: number | null;
  documentVersionStatus?: string | null;
  recipientStatus?: string | null;
  downloadCount?: number;
  lastDownloadedAt?: string | null;
}

export interface StatementListResponse {
  statements: CanonicalStatementApiRecord[];
  source: "canonical-d1";
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasNext?: boolean;
    hasPrevious?: boolean;
    applied?: {
      search?: string;
      status?: string;
      customer?: string;
      client?: string;
      account?: string;
      statementDate?: string;
      generatedDate?: string;
      versionStatus?: string;
      downloadStatus?: string;
      recipientStatus?: string;
      recipient?: string;
      sort?: string;
    };
  };
}

export interface CanonicalStatementEventRecord {
  eventId: string;
  companyId: string;
  statementId: string;
  accountId: string;
  eventType: string;
  previousStatus?: string | null;
  newStatus?: string | null;
  actorUserId?: string | null;
  actorName?: string | null;
  occurredAt?: string | null;
  metadata?: Record<string, unknown>;
  idempotencyKey?: string | null;
}

export interface CanonicalStatementDeliveryAttemptRecord {
  deliveryAttemptId: string;
  companyId: string;
  statementId: string;
  accountId: string;
  recipient: string;
  provider?: string | null;
  providerMessageId?: string | null;
  providerAcceptedAt?: string | null;
  deliveredAt?: string | null;
  deferredAt?: string | null;
  failedAt?: string | null;
  bouncedAt?: string | null;
  blockedAt?: string | null;
  status: string;
  errorCode?: string | null;
  safeErrorMessage?: string | null;
  attemptNumber?: number;
  idempotencyKey?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface CanonicalStatementDocumentRecord {
  documentId: string;
  companyId: string;
  statementId: string;
  accountId: string;
  fileName: string;
  contentType: string;
  storageReference: string;
  generatedAt: string;
  generatedBy?: string | null;
  checksum?: string | null;
  createdAt: string;
}

export interface CanonicalStatementDownloadPermissions {
  canView: boolean;
  canDownload: boolean;
  canDownloadHistorical: boolean;
  canViewAudit: boolean;
  canVerifyIntegrity: boolean;
}

export interface CanonicalStatementDownloadQueueItem {
  companyId: string;
  clientId?: string | null;
  customerId: string;
  accountId: string;
  placementId?: string | null;
  statementId: string;
  statementNumber: string;
  statementDate?: string | null;
  lifecycleStatus: string;
  archiveStatus: string;
  customerName: string;
  customerNumber: string;
  clientCompany: string;
  recipientName: string;
  recipientEmail: string;
  documentVersionId?: string | null;
  versionNumber?: number | null;
  documentStatus: string;
  isActive: boolean;
  mimeType?: string | null;
  fileSize?: number | null;
  generatedAt?: string | null;
  storageProvider?: string | null;
  legacyClassification?: "VERIFIED_AR_DOCUMENT" | "KNOWN_OTHER_PRODUCT" | "MISSING_OBJECT" | "UNKNOWN";
  storageKeyPresent: boolean;
  downloadCount: number;
  lastDownloadedAt?: string | null;
}

export interface CanonicalStatementDocumentVersionRecord {
  documentVersionId: string;
  companyId: string;
  statementId: string;
  accountId: string;
  versionNumber: number;
  documentStatus: string;
  isActive: boolean;
  contentHash?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  storageProvider?: string | null;
  legacyClassification?: "VERIFIED_AR_DOCUMENT" | "KNOWN_OTHER_PRODUCT" | "MISSING_OBJECT" | "UNKNOWN";
  storageKeyPresent: boolean;
  previewWatermark?: string | null;
  correctionReason?: string | null;
  supersedesDocumentVersionId?: string | null;
  supersededByDocumentVersionId?: string | null;
  generatedBy?: string | null;
  generatedAt?: string | null;
  createdAt: string;
}

export interface CanonicalStatementDownloadAuditRecord {
  auditId: string;
  documentVersionId?: string | null;
  versionNumber?: number | null;
  eventType: string;
  resultStatus: string;
  sourceWorkspace: string;
  actorUserId: string;
  actorName?: string | null;
  actorRole: string;
  correlationId: string;
  errorCode?: string | null;
  safeErrorMessage?: string | null;
  occurredAt: string;
}

export interface CanonicalStatementDownloadDetailResponse {
  statement: CanonicalStatementApiRecord;
  versions: CanonicalStatementDocumentVersionRecord[];
  audit: CanonicalStatementDownloadAuditRecord[];
  permissions: CanonicalStatementDownloadPermissions;
  source: "canonical-d1";
}

export interface CanonicalStatementDownloadReports {
  summary: {
    completedDownloads: number;
    failedDownloads: number;
    statementsDownloaded: number;
    neverDownloaded: number;
    historicalVersionDownloads: number;
  };
  downloadsByUser: Array<{ actor_name?: string | null; download_count: number }>;
  downloadsByClient: Array<{ client_company?: string | null; download_count: number }>;
  failedDownloads: Array<{
    statement_id: string;
    version_number?: number | null;
    safe_error_message?: string | null;
    occurred_at: string;
  }>;
  mostDownloadedStatements: Array<{
    statement_id: string;
    statement_number: string;
    customer_name: string;
    download_count: number;
  }>;
  neverDownloadedStatements: Array<{
    statement_id: string;
    statement_number: string;
    customer_name: string;
  }>;
}

export class CanonicalStatementApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "CanonicalStatementApiError";
    this.status = status;
  }
}

export type CanonicalStatementListSort =
  | "updated-desc"
  | "updated-asc"
  | "date-desc"
  | "date-asc"
  | "balance-desc"
  | "balance-asc"
  | "statement-number-desc"
  | "statement-number-asc"
  | "customer-desc"
  | "customer-asc"
  | "generated-desc"
  | "generated-asc"
  | "status-desc"
  | "status-asc";

export interface CanonicalStatementListOptions {
  accountId?: string;
  search?: string;
  status?: string;
  customer?: string;
  client?: string;
  statementDate?: string;
  generatedDate?: string;
  versionStatus?: string;
  downloadStatus?: string;
  recipientStatus?: string;
  recipient?: string;
  sort?: CanonicalStatementListSort;
  page?: number;
  pageSize?: number;
}

export interface CanonicalStatementCreationRequest {
  accountId: string;
  asOfDate: string;
  expectedCustomerId?: string;
  presentation?: {
    templateId?: string;
    recipientProfileId?: string;
    note?: string;
  };
  context?: {
    source?: string;
    returnContext?: string;
  };
  idempotencyKey: string;
  replacementReason?: string;
}

export type StatementReadinessOutcome = "CREATE_REQUIRED" | "CURRENT_EXISTS" | "REQUIRES_REVIEW" | "NO_QUALIFYING_INVOICES" | "MIXED_CURRENCY" | "INELIGIBLE_ACCOUNT" | "UNAUTHORIZED";

export interface CanonicalStatementReadiness {
  outcome: StatementReadinessOutcome;
  account: { accountId: string; accountNumber: string; customerId: string; customerName: string; clientId: string; clientName: string } | null;
  operationalDate: string;
  operationalTimeZone: string;
  eligible: boolean;
  qualifyingInvoiceCount: number;
  currency: string;
  reconciliation: { lineItemBalanceCents: number; accountBalanceCents: number; differenceCents: number; status: "Reconciled" } | null;
  sourceHash: string | null;
  currentStatement: CanonicalStatementApiRecord | null;
  snapshotState: "NONE" | "CURRENT" | "STALE";
  activeDocument: { versionId: string; versionNumber: number; status: string } | null;
  latestSend: { attemptId: string; status: string; requestedAt: string | null; retryable: boolean } | null;
  permissions: { canCreate: boolean; canReplace: boolean; canGenerate: boolean; canSend: boolean };
  reasons: Array<{ code: string; message: string }>;
}

export interface CanonicalStatementTemplateSummary {
  id: string;
  templateName: string;
  templateType: string;
  status: string;
  approvalStatus: string;
  activeVersionId: string | null;
}

export interface CanonicalStatementCreationResponse {
  statement: CanonicalStatementApiRecord;
  created: boolean;
  idempotentReplay: boolean;
  reconciliation?: {
    lineItemBalanceCents: number;
    accountBalanceCents: number;
    differenceCents: number;
    status: "Reconciled";
  };
  source: "canonical-d1";
}

export async function createCanonicalARRecoveryStatement(request: CanonicalStatementCreationRequest, requestedCompanyId?: string | null) {
  const params = new URLSearchParams();
  if (requestedCompanyId) params.set("companyId", requestedCompanyId);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return fetchCanonicalJson<CanonicalStatementCreationResponse>(`/api/ar-recovery/statements${suffix}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(request),
  });
}

export async function assessCanonicalARRecoveryStatementReadiness(accountId: string, options: { asOfDate?: string; customerId?: string; companyId?: string | null } = {}) {
  const params = new URLSearchParams({ accountId });
  if (options.asOfDate) params.set("asOfDate", options.asOfDate);
  if (options.customerId) params.set("customerId", options.customerId);
  if (options.companyId) params.set("companyId", options.companyId);
  const payload = await fetchCanonicalJson<{ readiness: CanonicalStatementReadiness; source: "canonical-d1" }>(`/api/ar-recovery/statements/readiness?${params}`);
  return payload.readiness;
}

export async function listCanonicalStatementTemplates(companyId?: string | null) {
  const params = new URLSearchParams();
  if (companyId) params.set("companyId", companyId);
  const payload = await fetchCanonicalJson<{ templates: CanonicalStatementTemplateSummary[]; source: "canonical-d1" }>(`/api/ar-recovery/statements/templates?${params}`);
  return payload.templates;
}

export async function validateCanonicalStatement(statementId: string, idempotencyKey: string, companyId?: string | null) {
  return statementCommand(statementId, "validate", { idempotencyKey, companyId });
}

export async function prepareCanonicalStatement(statementId: string, idempotencyKey: string, version?: number, companyId?: string | null) {
  return statementCommand(statementId, "prepare", { idempotencyKey, version, companyId });
}

export async function generateCanonicalStatementDocument(statementId: string, idempotencyKey: string, companyId?: string | null) {
  return statementCommand(statementId, "documents/generate", { idempotencyKey, companyId });
}

export async function regenerateCanonicalStatementDocument(statementId: string, idempotencyKey: string, correctionReason: string, companyId?: string | null) {
  return statementCommand(statementId, "documents/regenerate", { idempotencyKey, correctionReason, companyId });
}

async function statementCommand<T = Record<string, unknown>>(statementId: string, command: string, body: Record<string, unknown>) {
  return fetchCanonicalJson<T>(`/api/ar-recovery/statements/${encodeURIComponent(statementId)}/${command}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function listCanonicalARRecoveryStatements(companyId?: string | null, options: CanonicalStatementListOptions = {}) {
  const payload = await listCanonicalARRecoveryStatementRecords(companyId, options);
  return payload.statements.map(mapCanonicalStatementToWorkspaceRecord);
}

export async function listCanonicalARRecoveryStatementRecords(companyId?: string | null, options: CanonicalStatementListOptions = {}) {
  const params = new URLSearchParams();
  if (companyId) params.set("companyId", companyId);
  if (options.accountId) params.set("accountId", options.accountId);
  if (options.search) params.set("search", options.search);
  if (options.status) params.set("status", options.status);
  if (options.customer) params.set("customer", options.customer);
  if (options.client) params.set("client", options.client);
  if (options.statementDate) params.set("statementDate", options.statementDate);
  if (options.generatedDate) params.set("generatedDate", options.generatedDate);
  if (options.versionStatus) params.set("versionStatus", options.versionStatus);
  if (options.downloadStatus) params.set("downloadStatus", options.downloadStatus);
  if (options.recipientStatus) params.set("recipientStatus", options.recipientStatus);
  if (options.recipient) params.set("recipient", options.recipient);
  if (options.sort) params.set("sort", options.sort);
  if (options.page) params.set("page", String(options.page));
  if (options.pageSize) params.set("pageSize", String(options.pageSize));
  return fetchCanonicalJson<StatementListResponse>(`/api/ar-recovery/statements?${params.toString()}`);
}

export async function getCanonicalARRecoveryStatement(statementId: string, companyId?: string | null) {
  const params = new URLSearchParams();
  if (companyId) params.set("companyId", companyId);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const payload = await fetchCanonicalJson<{ statement: CanonicalStatementApiRecord; source: "canonical-d1" }>(`/api/ar-recovery/statements/${encodeURIComponent(statementId)}${suffix}`);
  return payload.statement;
}

export async function listCanonicalARRecoveryStatementEvents(statementId: string, companyId?: string | null) {
  const params = new URLSearchParams();
  if (companyId) params.set("companyId", companyId);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const payload = await fetchCanonicalJson<{ events: CanonicalStatementEventRecord[]; source: "canonical-d1" }>(`/api/ar-recovery/statements/${encodeURIComponent(statementId)}/events${suffix}`);
  return payload.events;
}

export async function listCanonicalARRecoveryStatementDeliveryAttempts(statementId: string, companyId?: string | null) {
  const params = new URLSearchParams();
  if (companyId) params.set("companyId", companyId);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const payload = await fetchCanonicalJson<{ deliveryAttempts: CanonicalStatementDeliveryAttemptRecord[]; source: "canonical-d1" }>(`/api/ar-recovery/statements/${encodeURIComponent(statementId)}/delivery-attempts${suffix}`);
  return payload.deliveryAttempts;
}

export async function listCanonicalARRecoveryStatementDocuments(statementId: string, companyId?: string | null) {
  const params = new URLSearchParams();
  if (companyId) params.set("companyId", companyId);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const payload = await fetchCanonicalJson<{ documents: CanonicalStatementDocumentRecord[]; source: "canonical-d1" }>(`/api/ar-recovery/statements/${encodeURIComponent(statementId)}/documents${suffix}`);
  return payload.documents;
}

export async function listCanonicalStatementDownloads(
  companyId?: string | null,
  options: {
    search?: string;
    client?: string;
    customer?: string;
    status?: string;
    version?: string;
    generatedDate?: string;
    downloadedDate?: string;
    downloadStatus?: string;
    account?: string;
    recipient?: string;
    page?: number;
    pageSize?: number;
  } = {},
) {
  const params = new URLSearchParams();
  if (companyId) params.set("companyId", companyId);
  Object.entries(options).forEach(([key, value]) => {
    if (value) params.set(key, String(value));
  });
  return fetchCanonicalJson<{
    items: CanonicalStatementDownloadQueueItem[];
    permissions: CanonicalStatementDownloadPermissions;
    pagination: { page: number; pageSize: number; total: number; totalPages: number };
    source: "canonical-d1";
  }>(`/api/ar-recovery/statements/downloads?${params.toString()}`);
}

export async function getCanonicalStatementDownloadDetail(statementId: string, companyId?: string | null) {
  const params = new URLSearchParams();
  if (companyId) params.set("companyId", companyId);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return fetchCanonicalJson<CanonicalStatementDownloadDetailResponse>(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/downloads${suffix}`,
  );
}

export async function getCanonicalStatementDownloadReports(companyId?: string | null) {
  const params = new URLSearchParams({ view: "reports" });
  if (companyId) params.set("companyId", companyId);
  const payload = await fetchCanonicalJson<{ reports: CanonicalStatementDownloadReports; source: "canonical-d1" }>(
    `/api/ar-recovery/statements/downloads?${params.toString()}`,
  );
  return payload.reports;
}

export async function downloadCanonicalStatementVersion(
  statementId: string,
  versionId: string,
  options: { companyId?: string | null; sourceWorkspace?: string; correlationId?: string } = {},
) {
  const params = new URLSearchParams();
  if (options.companyId) params.set("companyId", options.companyId);
  if (options.sourceWorkspace) params.set("sourceWorkspace", options.sourceWorkspace);
  if (options.correlationId) params.set("correlationId", options.correlationId);
  const response = await fetch(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/downloads/${encodeURIComponent(versionId)}?${params.toString()}`,
    { credentials: "include", headers: { Accept: "application/pdf" } },
  );
  if (!response.ok) throw new CanonicalStatementApiError(response.status, await response.text() || "Statement download failed.");
  const disposition = response.headers.get("Content-Disposition") || "";
  const fileName = disposition.match(/filename="([^"]+)"/i)?.[1] || `${statementId}.pdf`;
  return { blob: await response.blob(), fileName, correlationId: response.headers.get("X-Correlation-ID") || "" };
}

export function previewCanonicalStatementVersionUrl(
  statementId: string,
  versionId: string,
  options: { companyId?: string | null; sourceWorkspace?: string; correlationId?: string } = {},
) {
  const params = new URLSearchParams();
  if (options.companyId) params.set("companyId", options.companyId);
  if (options.sourceWorkspace) params.set("sourceWorkspace", options.sourceWorkspace);
  if (options.correlationId) params.set("correlationId", options.correlationId);
  return `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/documents/${encodeURIComponent(versionId)}/preview?${params.toString()}`;
}

export async function verifyCanonicalStatementVersion(
  statementId: string,
  versionId: string,
  options: { companyId?: string | null; sourceWorkspace?: string; correlationId?: string } = {},
) {
  const params = new URLSearchParams();
  if (options.companyId) params.set("companyId", options.companyId);
  if (options.sourceWorkspace) params.set("sourceWorkspace", options.sourceWorkspace);
  if (options.correlationId) params.set("correlationId", options.correlationId);
  const response = await fetch(
    `/api/ar-recovery/statements/${encodeURIComponent(statementId)}/downloads/${encodeURIComponent(versionId)}/verify?${params.toString()}`,
    { method: "POST", credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok) throw new CanonicalStatementApiError(response.status, await response.text() || "Statement integrity verification failed.");
  return response.json() as Promise<{
    integrity: {
      verified: boolean;
      documentVersionId: string;
      versionNumber: number;
      checksum: string;
      fileSize: number;
      storageProvider?: string | null;
      checkedAt: string;
      correlationId: string;
    };
    source: "canonical-r2";
  }>;
}

export async function importCanonicalARRecoveryStatements(companyId: string, statements: ARRecoveryStatementRecord[], confirmed: boolean) {
  const response = await fetch("/api/ar-recovery/statements/import", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      companyId,
      confirmed,
      source: "legacy-browser-reviewed-import",
      statements: statements.map(mapWorkspaceStatementToCanonical),
    }),
  });
  if (!response.ok) throw new Error(await response.text() || "Unable to import canonical statements.");
  return response.json();
}

export function mapWorkspaceStatementToCanonical(statement: ARRecoveryStatementRecord): CanonicalStatementApiRecord {
  return {
    companyId: statement.companyId,
    clientId: statement.companyId,
    customerId: statement.customerNumber,
    accountId: statement.accountId,
    statementId: statement.id,
    statementNumber: statement.statementNumber,
    statementType: "Account Statement",
    statementDate: statement.dateSent || statement.createdAt.slice(0, 10),
    asOfDate: statement.createdAt.slice(0, 10),
    currency: "USD",
    balanceCents: statement.balanceCents,
    openInvoiceCount: statement.openInvoiceCount,
    recipientName: statement.mergeFields.contactName,
    recipientEmail: statement.mergeFields.email,
    deliveryOption: "Email",
    lifecycleStatus: statement.status,
    deliveryStatus: statement.deliveryStatus,
    openedStatus: statement.openedStatus,
    downloadedStatus: statement.downloadedStatus,
    customerName: statement.customerName,
    customerNumber: statement.customerNumber,
    clientCompany: statement.clientCompany,
    collector: statement.collector,
    manager: statement.manager,
    notes: statement.notes,
    mergeFields: statement.mergeFields,
    lineItems: statement.lineItems.map((item, index) => ({
      invoiceNumber: item.invoiceNumber,
      dueDate: item.dueDate,
      originalAmountCents: item.amountCents,
      remainingBalanceCents: item.amountCents,
      agingBucket: item.status,
      sortOrder: index,
    })),
    generatedAt: statement.createdAt,
    generatedByName: statement.sentBy || statement.collector,
    sentAt: statement.sentAt,
    sentByName: statement.sentBy,
    source: "legacy-browser-or-seeded-statement",
    legacyImportMetadata: {
      originalId: statement.id,
      originalStatus: statement.status,
      originalDeliveryStatus: statement.deliveryStatus,
    },
    idempotencyKey: `legacy-statement:${statement.companyId}:${statement.id}`,
    createdAt: statement.createdAt,
    updatedAt: statement.updatedAt,
  };
}

export function mapCanonicalStatementToWorkspaceRecord(statement: CanonicalStatementApiRecord): ARRecoveryStatementRecord {
  const mergeFields = {
    businessName: stringValue(statement.mergeFields?.businessName),
    dbaName: stringValue(statement.mergeFields?.dbaName),
    address: stringValue(statement.mergeFields?.address),
    phone: stringValue(statement.mergeFields?.phone),
    email: stringValue(statement.mergeFields?.email || statement.recipientEmail),
    website: stringValue(statement.mergeFields?.website),
    contactName: stringValue(statement.mergeFields?.contactName || statement.recipientName),
  };
  const createdAt = statement.createdAt || statement.generatedAt || new Date().toISOString();
  const updatedAt = statement.updatedAt || createdAt;
  return {
    id: statement.statementId,
    companyId: statement.companyId,
    accountId: statement.accountId,
    customerName: statement.customerName,
    customerNumber: statement.customerNumber,
    clientCompany: statement.clientCompany || "",
    statementNumber: statement.statementNumber,
    status: normalizeStatementStatus(statement.lifecycleStatus),
    sentAt: statement.sentAt || null,
    dateSent: statement.statementDate || statement.sentAt?.slice(0, 10) || null,
    timeSent: statement.sentAt ? formatCanonicalTime(statement.sentAt) : null,
    sentBy: statement.sentByName || statement.generatedByName || statement.collector || "Statement Center",
    deliveryStatus: normalizeDeliveryStatus(statement.deliveryStatus),
    openedStatus: normalizeOpenedStatus(statement.openedStatus || statement.deliveryStatus),
    downloadedStatus: normalizeDownloadedStatus(statement.downloadedStatus || statement.deliveryStatus),
    balanceCents: statement.balanceCents,
    openInvoiceCount: statement.openInvoiceCount,
    dueDate: statement.asOfDate || statement.statementDate || createdAt.slice(0, 10),
    collector: statement.collector || "Unassigned",
    manager: statement.manager || "Unassigned",
    notes: statement.notes || "",
    lineItems: (statement.lineItems || []).map((item) => ({
      invoiceNumber: item.invoiceNumber,
      dueDate: item.dueDate || item.invoiceDate || createdAt.slice(0, 10),
      amountCents: item.remainingBalanceCents ?? item.originalAmountCents,
      status: normalizeLineItemStatus(item.agingBucket || ""),
    })),
    mergeFields,
    createdAt,
    updatedAt,
  };
}

async function fetchCanonicalJson<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    credentials: "include",
    headers: { Accept: "application/json", ...init.headers },
  });
  if (!response.ok) throw new CanonicalStatementApiError(response.status, await response.text() || "Unable to load canonical statements.");
  return response.json() as Promise<T>;
}

function stringValue(value: unknown) {
  return typeof value === "string" ? value : "";
}

function normalizeStatementStatus(value: string): ARRecoveryStatementRecord["status"] {
  const allowed: ARRecoveryStatementRecord["status"][] = ["Draft", "Created", "Queued", "Generated", "Delivery Not Configured", "Sent", "Delivered", "Viewed", "Opened", "Downloaded", "Failed", "Cancelled"];
  return allowed.includes(value as ARRecoveryStatementRecord["status"]) ? value as ARRecoveryStatementRecord["status"] : "Created";
}

function normalizeDeliveryStatus(value: string): ARRecoveryStatementRecord["deliveryStatus"] {
  const allowed: ARRecoveryStatementRecord["deliveryStatus"][] = ["Pending", "Sent", "Delivered", "Opened", "Downloaded", "Failed"];
  return allowed.includes(value as ARRecoveryStatementRecord["deliveryStatus"]) ? value as ARRecoveryStatementRecord["deliveryStatus"] : "Pending";
}

function normalizeOpenedStatus(value: string): ARRecoveryStatementRecord["openedStatus"] {
  return value === "Opened" ? "Opened" : "Pending";
}

function normalizeDownloadedStatus(value: string): ARRecoveryStatementRecord["downloadedStatus"] {
  return value === "Downloaded" ? "Downloaded" : "Pending";
}

function normalizeLineItemStatus(value: string): ARRecoveryStatementRecord["lineItems"][number]["status"] {
  if (value === "Past Due" || value === "Paid" || value === "Partial") return value;
  return "Open";
}

function formatCanonicalTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}
